import {type JSX, useCallback, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useCurrentRoute} from '~/CreateFabricInvitation/navigation';
import {
  selectCohostName,
  selectHost,
  selectPhoneNumber,
} from '~/CreateFabricInvitation/reducers/details/selectors';
import {selectUsedAs} from '~/CreateFabricInvitation/reducers/fabricTemplate/selectors';
import {HostDetailsOverlay} from '~/CreateFabricInvitation/routes/EventDetails/components/HostDetails/HostDetailsOverlay';
import {InvitationWidget} from '~/common/components/InvitationWidget/InvitationWidget';
import {useFormValidations} from '~/common/FabricInvitation/hooks/useFormValidations';
import {validateHost} from '~/common/FabricInvitation/utils/validation';
import {GuestOptionsOverlay} from '~/common/fabric/EventGuests/components/GuestOptions/GuestOptionsOverlay';
import {eventInEditMode, selectRsvpOff} from '~/common/fabric/reducers/details/selectors';
import {selectRSVPDeadline} from '~/common/fabric/reducers/guestOptions/selectors';
import {isSaveTheDateByUsedAs} from '~/common/utils/event_utils';
import {Typography} from '~library/atoms/Typography';

export const HostDetails = (): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const {id} = useCurrentRoute();
  const hostName = useSelector(selectHost);
  const cohostName = useSelector(selectCohostName);
  const phoneNumber = useSelector(selectPhoneNumber);
  const usedAs = useSelector(selectUsedAs);
  const isSaveTheDate = isSaveTheDateByUsedAs(usedAs);
  const rsvpOff: boolean = useSelector(selectRsvpOff);
  const inEditMode: boolean = useSelector(eventInEditMode);
  const rsvpDeadline = useSelector(selectRSVPDeadline);
  const [showHostDetailsOverlay, setShowHostDetailsOverlay] = useState(false);
  const [showGuestOptionsOverlay, setShowGuestOptionsOverlay] = useState(false);

  const combinedHostName = cohostName
    ? `${hostName.trim()} & ${cohostName.trim()}`
    : hostName.trim();

  let guestSettingsSubtitle = `${rsvpDeadline ? 'Edit' : 'Add'} RSVP deadline, adjust plus ones & more`;
  if (isSaveTheDate) {
    guestSettingsSubtitle = 'Adjust plus ones, privacy, and more';
  }

  const toggleHostDetailsOverlay = useCallback(() => {
    setShowHostDetailsOverlay((prev) => !prev);
  }, []);

  const validate = useCallback(() => {
    return validateHost(hostName, ref.current);
  }, [hostName]);

  useFormValidations('host', validate);

  const toggleGuestOptionsOverlay = useCallback(() => {
    setShowGuestOptionsOverlay((prev) => !prev);
  }, []);

  let subtitle = '';
  if (!phoneNumber && !cohostName) {
    subtitle = 'Add a phone number and/or co-host';
  } else if (phoneNumber && !cohostName) {
    subtitle = 'Edit phone number and/or add co-host';
  } else if (!phoneNumber && cohostName) {
    subtitle = 'Add phone number and/or edit co-host';
  } else if (phoneNumber && cohostName) {
    subtitle = 'Edit phone number and/or co-host';
  }

  return (
    <>
      <div className="create-flow__form-section  event-details__hosts" ref={ref}>
        <Typography variant="label2" className="small input-label">
          Host details
        </Typography>
        <InvitationWidget.Toggle
          filled
          editable
          title={combinedHostName || 'Host name(s)'}
          subtitle={subtitle}
          qaId="event-host-details"
          onClick={toggleHostDetailsOverlay}
          overlay={
            <HostDetailsOverlay
              closeOverlay={toggleHostDetailsOverlay}
              isOpen={showHostDetailsOverlay}
              title="Host details"
            />
          }
        />
      </div>
      {inEditMode && rsvpOff ? null : (
        <div className="create-flow__form-section event-details__rsvp-options">
          <Typography variant="label2" className="small input-label">
            RSVP options
          </Typography>
          <InvitationWidget.Toggle
            filled
            editable
            title="Manage guest settings"
            subtitle={guestSettingsSubtitle}
            qaId={`guest-options-step-${id}`}
            onClick={toggleGuestOptionsOverlay}
            overlay={
              <GuestOptionsOverlay
                closeOverlay={() => setShowGuestOptionsOverlay(false)}
                isOpen={showGuestOptionsOverlay}
              />
            }
          />
        </div>
      )}
    </>
  );
};
